// frontend/src/pages/auth/RegisterPage.tsx

import React from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Alert,
  Container,
  CssBaseline,
  IconButton,
  Step,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
} from "@mui/material";
import { PersonAdd as PersonAddIcon } from "@mui/icons-material";
import {
  VERDE_MENTA,
  VERDE_MENTA_CLARO,
  FUNDO_ESCURO,
  FUNDO_PAPEL,
  TEXTO_SECUNDARIO,
  withAlpha,
} from "../../themes/themeConstants";
import { useRegisterForm } from "../../hooks/auth/useRegisterForm";
import LanguageSelector from "../../components/auth/register/LanguageSelector";
import AccountDataStep from "../../components/auth/register/AccountDataStep";
import ProjectInfoStep from "../../components/auth/register/ProjectInfoStep";
import {
  backButtonSx,
  submitButtonSx,
  errorAlertSx,
} from "../../components/auth/register/registerStyles";

const HOME_ICON_SX = {
  color: VERDE_MENTA,
  background: withAlpha(VERDE_MENTA, 0.1),
  "&:hover": { background: withAlpha(VERDE_MENTA, 0.2) },
};

const RegisterPage: React.FC = () => {
  const {
    formik, activeStep, showPassword, setShowPassword,
    passwordValidation, setPasswordValidation, loading, error,
    emailExists, setEmailExists, checkingEmail, setCheckingEmail,
    currentLang, setCurrentLang, t, prefilledData,
    checkEmailExists, handleForgotPassword, handleBack, goToLogin,
  } = useRegisterForm();

  const isLastStep = activeStep === Object.values(t.steps).length - 1;
  const submitDisabled = Boolean(
    loading || checkingEmail ||
    (activeStep === 0 && emailExists) ||
    (activeStep === 0 && passwordValidation && !passwordValidation.isValid)
  );

  return (
    <Box sx={{
      minHeight: "100vh", width: "100vw", display: "flex",
      alignItems: "center", justifyContent: "center",
      backgroundColor: FUNDO_ESCURO, backgroundSize: "cover",
      backgroundPosition: "center", backgroundAttachment: "fixed",
      overflow: "auto", py: 2,
    }}>
      <CssBaseline />

      <Box sx={{ position: "absolute", top: 20, left: 20 }}>
        <Tooltip title="Voltar para a página inicial">
          <IconButton onClick={() => { window.location.href = "/"; }} size="medium" sx={HOME_ICON_SX}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
          </IconButton>
        </Tooltip>
      </Box>

      <LanguageSelector currentLang={currentLang} onChangeLang={setCurrentLang} />

      <Container maxWidth="sm" sx={{
        backgroundColor: FUNDO_PAPEL, borderRadius: 3,
        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
        border: `1px solid ${withAlpha(VERDE_MENTA, 0.2)}`,
        p: 3, my: 2, maxHeight: "95vh", overflow: "auto",
      }}>
        <Box sx={{ textAlign: "center", mb: 1.5 }}>
          <Avatar sx={{ mx: "auto", bgcolor: withAlpha(VERDE_MENTA, 0.2), color: VERDE_MENTA }}>
            <PersonAddIcon />
          </Avatar>
          <Typography variant="h4" component="h1"
            sx={{ color: VERDE_MENTA, fontWeight: 600, letterSpacing: 1, mt: 1 }}>
            {t.registerTitle}
          </Typography>
          {prefilledData && (
            <Typography variant="subtitle1"
              sx={{ color: VERDE_MENTA, fontWeight: 500, mt: 1, opacity: 0.8, textAlign: "center" }}>
              Editando dados coletados pelo LEX
            </Typography>
          )}
        </Box>

        {error && <Alert severity="error" sx={errorAlertSx}>{error}</Alert>}

        <Stepper activeStep={activeStep} sx={{
          mb: 2,
          "& .MuiStepLabel-label": {
            color: TEXTO_SECUNDARIO, fontSize: "0.8rem",
            "&.Mui-active": { color: VERDE_MENTA },
            "&.Mui-completed": { color: VERDE_MENTA_CLARO },
          },
          "& .MuiStep-root": { px: 1 },
        }}>
          {Object.values(t.steps).map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        <Box component="form" onSubmit={formik.handleSubmit} noValidate>
          {activeStep === 0 && (
            <AccountDataStep
              formik={formik} t={t}
              emailExists={emailExists} checkingEmail={checkingEmail}
              showPassword={showPassword} setShowPassword={setShowPassword}
              checkEmailExists={checkEmailExists} setEmailExists={setEmailExists}
              setCheckingEmail={setCheckingEmail}
              handleForgotPassword={handleForgotPassword}
              setPasswordValidation={setPasswordValidation}
            />
          )}
          {activeStep === 1 && <ProjectInfoStep formik={formik} t={t} />}

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
            <Button disabled={activeStep === 0} onClick={handleBack}
              variant="outlined" sx={backButtonSx(TEXTO_SECUNDARIO)}>
              {t.back}
            </Button>
            <Button type="submit" variant="contained" disabled={submitDisabled} sx={submitButtonSx}>
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : checkingEmail ? "Verificando..." : isLastStep ? t.finish : t.next}
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1, mt: 1.5 }}>
          <Typography variant="body2" sx={{ color: TEXTO_SECUNDARIO, fontWeight: 400 }}>
            {t.alreadyHaveAccount}
          </Typography>
          <Button onClick={goToLogin} sx={{
            color: VERDE_MENTA, fontWeight: 500, textTransform: "none",
            p: 0, minWidth: "auto",
            "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
          }}>
            {t.login}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default RegisterPage;
